import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { ConnectionCard } from "@/components/ConnectionCard";
import { CreateConnectionModal } from "@/components/CreateConnectionModal";
import { EmptyState } from "@/components/EmptyState";
import { PhoneVerificationModal } from "@/components/PhoneVerificationModal";
import { useConnections } from "@/hooks/useConnections";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Plus, ShieldAlert, Shield, Users, Wifi, WifiOff, ChevronRight, Activity, Clock, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";
import AppSidebar from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { SettingsNotificationsTab } from "@/components/SettingsNotificationsTab";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LanguageSwitcherSettings } from "@/components/LanguageSwitcher";
import { ProfileSettings } from "@/components/ProfileSettings";
import { useTranslation } from "react-i18next";
import { db, requestNotificationPermission } from "@/lib/firebase";
import { getDoc, doc, setDoc } from "firebase/firestore";
import { cleanupRecaptcha } from "@/lib/phoneAuth";
import { useNotificationPreferencesContext } from "@/context/NotificationPreferencesContext";
import { useIncidentContext } from "@/context/IncidentContext";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

interface DashboardProps {
  showConnections?: boolean;
  showSettings?: boolean;
}


export default function Dashboard({ showConnections = false, showSettings = false }: DashboardProps) {
  const [isCreateConnectionModalOpen, setIsCreateConnectionModalOpen] = useState(false);
  const [isPhoneVerificationModalOpen, setIsPhoneVerificationModalOpen] = useState(false);
  const [reconnectData, setReconnectData] = useState<{ id: string; name: string } | null>(null);

  const { connections, loading, error, createConnection, deleteConnection, updateConnection, reconnectConnection } = useConnections();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { preferences, updateWhatsAppPreferences, isLoading } = useNotificationPreferencesContext();
  const { incidents, summary: incidentSummary, loading: incidentsLoading } = useIncidentContext();
  const navigate = useNavigate();


  const handleCreateConnectionModalClose = (connectionCreated = false) => {
    setIsCreateConnectionModalOpen(false);
    setReconnectData(null); // Clear reconnection state when closing modal
    
    console.log('DEBUG: handleCreateConnectionModalClose called with:', {
      connectionCreated,
      isLoading,
      isVerified: preferences.whatsapp.isVerified,
      isPhoneVerificationModalOpen
    });
    
    // Only show verification modal if:
    // 1. A connection was created successfully
    // 2. Preferences are not loading
    // 3. User is not verified
    // 4. Verification modal is not already open
    if (connectionCreated && 
        !isLoading && 
        !preferences.whatsapp.isVerified &&
        !isPhoneVerificationModalOpen) {
      console.log('DEBUG: Conditions met, setting timeout for phone verification modal');
      setTimeout(() => {
        console.log('DEBUG: Timeout executed, checking verification status again:', {
          isVerified: preferences.whatsapp.isVerified,
          isPhoneVerificationModalOpen
        });
        // Verificar nuevamente el estado antes de mostrar el modal
        // para evitar condiciones de carrera
        if (!preferences.whatsapp.isVerified && !isPhoneVerificationModalOpen) {
          console.log('DEBUG: Opening phone verification modal');
          setIsPhoneVerificationModalOpen(true);
        } else {
          console.log('DEBUG: Not opening modal - user is now verified or modal already open');
        }
      }, 2000);
    } else {
      console.log('DEBUG: Conditions not met for showing phone verification modal');
    }
  };

  useEffect(() => {
    // This useEffect is no longer needed as the PhoneVerificationModal is triggered by CreateConnectionModal's onClose
  }, [connections]);

  // Remove the loadPreferences useEffect as it's now handled by the context

  
  useEffect(() => {
    return () => {
      cleanupRecaptcha();
    };
  }, []);


  const handleCreateConnection = async (name: string, ageRating: string) => {
    try {
      const id = await createConnection(name, ageRating);
      console.log("Connection created with ID:", id);
      return id;
    } catch (err) {
      console.error("Error in handleCreateConnection:", err);
      return undefined;
    }
  };

  const handleReconnectConnection = async (id: string) => {
    try {
      const newId = await reconnectConnection(id);
      console.log("Connection reconnected with new ID:", newId);
      return newId;
    } catch (err) {
      console.error("Error in handleReconnectConnection:", err);
      toast({
        title: "Reconnection failed",
        description: err instanceof Error ? err.message : "Failed to reconnect to WhatsApp",
        variant: "destructive",
      });
      return undefined;
    }
  };

  const handleConnectionReconnect = async (id: string): Promise<void> => {
    const connection = connections.find(conn => conn.id === id);
    if (connection) {
      setReconnectData({ id: connection.id, name: connection.name });
      setIsCreateConnectionModalOpen(true);
    }
  };

  const renderDashboardContent = () => {
    const connectedCount = connections.filter(c => c.status === 'connected').length;
    const disconnectedCount = connections.filter(c => c.status !== 'connected').length;
    const recentIncidents = incidents.slice(0, 5);

    // Build connection name lookup
    const connectionNames: Record<string, string> = {};
    for (const c of connections) {
      connectionNames[c.id] = c.name;
    }

    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>

        {/* Top summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Active Incidents */}
          <Card
            className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-red-500"
            onClick={() => navigate('/incidents')}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Incidents</CardTitle>
              <ShieldAlert className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              {incidentsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{incidentSummary.total}</div>
                  {incidentSummary.bySeverity.critical > 0 && (
                    <p className="text-xs text-red-600 font-medium mt-1">
                      {incidentSummary.bySeverity.critical} critical
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Pending Review */}
          <Card
            className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-amber-500"
            onClick={() => navigate('/incidents')}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending Review</CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              {incidentsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{incidentSummary.byStatus.PENDING}</div>
                  {incidentSummary.byStatus.ACTIVE > 0 && (
                    <p className="text-xs text-amber-600 font-medium mt-1">
                      {incidentSummary.byStatus.ACTIVE} active
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Connected Children */}
          <Card
            className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-500"
            onClick={() => navigate('/connections')}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Connections</CardTitle>
              <Users className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{connections.length}</div>
                  <div className="flex items-center gap-2 mt-1">
                    {connectedCount > 0 && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <Wifi className="h-3 w-3" /> {connectedCount}
                      </span>
                    )}
                    {disconnectedCount > 0 && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <WifiOff className="h-3 w-3" /> {disconnectedCount}
                      </span>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Severity Breakdown */}
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">By Severity</CardTitle>
              <Activity className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              {incidentsLoading ? (
                <Skeleton className="h-8 w-full" />
              ) : incidentSummary.total === 0 ? (
                <p className="text-sm text-muted-foreground">No incidents</p>
              ) : (
                <div className="flex items-center gap-3 text-xs">
                  {incidentSummary.bySeverity.critical > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full bg-red-500" />
                      <span className="font-medium">{incidentSummary.bySeverity.critical}</span> critical
                    </span>
                  )}
                  {incidentSummary.bySeverity.high > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full bg-orange-500" />
                      <span className="font-medium">{incidentSummary.bySeverity.high}</span> high
                    </span>
                  )}
                  {incidentSummary.bySeverity.medium > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full bg-amber-500" />
                      <span className="font-medium">{incidentSummary.bySeverity.medium}</span> med
                    </span>
                  )}
                  {incidentSummary.bySeverity.low > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                      <span className="font-medium">{incidentSummary.bySeverity.low}</span> low
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Incidents */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Incidents</CardTitle>
              <CardDescription>Latest flagged activity across all connections</CardDescription>
            </div>
            {incidents.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/incidents')}>
                View all <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {incidentsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : recentIncidents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Shield className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No incidents detected yet.</p>
                <p className="text-xs mt-1">AI monitoring is active across your connections.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentIncidents.map(incident => (
                  <div
                    key={incident.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/incidents/${incident.connection_id}/${incident.conversation_id}/${incident.id}`)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                        incident.severity === 'critical' ? 'bg-red-500' :
                        incident.severity === 'high' ? 'bg-orange-500' :
                        incident.severity === 'medium' ? 'bg-amber-500' : 'bg-blue-500'
                      }`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium truncate">
                            {incident.context?.conversation_name || incident.conversation_id}
                          </p>
                          {connectionNames[incident.connection_id] && (
                            <span className="text-[10px] px-1.5 py-0 h-4 inline-flex items-center rounded-md bg-muted text-muted-foreground font-normal shrink-0">
                              {connectionNames[incident.connection_id]}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className={`capitalize font-medium ${
                            incident.severity === 'critical' ? 'text-red-600' :
                            incident.severity === 'high' ? 'text-orange-600' : ''
                          }`}>
                            {incident.severity}
                          </span>
                          <span>·</span>
                          <span>{incident.message_count} message{incident.message_count !== 1 ? 's' : ''}</span>
                          <span>·</span>
                          <span>{format(incident.created_at, 'MMM d, HH:mm')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        incident.status === 'PENDING' ? 'bg-amber-100 text-amber-700' :
                        incident.status === 'ACTIVE' ? 'bg-red-100 text-red-700' :
                        incident.status === 'ESCALATED' ? 'bg-purple-100 text-purple-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {incident.status}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        {connections.length === 0 && !loading && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center py-8">
              <Users className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-sm font-medium mb-1">No connections yet</p>
              <p className="text-xs text-muted-foreground mb-4">Connect a WhatsApp account to start monitoring</p>
              <Button onClick={() => navigate('/connections')}>
                <Plus className="mr-2 h-4 w-4" /> Add Connection
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderSettingsContent = () => {
    return (
      <div className="max-w-7xl mx-auto space-y-8">
        <h1 className="text-2xl font-bold mb-6">Settings</h1>
        
        <SettingsNotificationsTab showOnlyInstallSection={true} />
        
        <Card>
          <CardHeader>
            <CardTitle>Profile Settings</CardTitle>
            <CardDescription>Manage your personal profile settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ProfileSettings />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Notification Settings</CardTitle>
            <CardDescription>Configure how you want to receive notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingsNotificationsTab showOnlyNotificationSettings={true} />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Account Settings</CardTitle>
            <CardDescription>Manage your account preferences and security</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
          <LanguageSwitcherSettings />
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderConnectionsContent = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="glass rounded-xl p-4 animate-pulse-subtle">
              <div className="space-y-4">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-3/4" />
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }

    if (!loading && connections.length === 0) {
      console.log("Showing empty state: loading=false, connections=0");
      return <EmptyState onCreateConnection={() => setIsCreateConnectionModalOpen(true)} />;
    }
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {connections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              onDelete={async (id) => {
                try {
                  await deleteConnection(id);
                } catch (err) {
                  console.error("Error deleting connection:", err);
                }
              }}
              onUpdate={async (id, data) => {
                try {
                  await updateConnection(id, data);
                } catch (err) {
                  console.error("Error updating connection:", err);
                }
              }}
              onReconnect={handleConnectionReconnect}
            />
          ))}
        </div>
        
        {/* Add Another Child button for populated state */}
        <div className="flex flex-col items-center pt-4">
          {connections.length > 1 && (
            <p className="text-sm text-muted-foreground mb-3 text-center">
              {t('helpers.multipleChildren')}
            </p>
          )}
          <Button 
            onClick={() => {
              setReconnectData(null);
              setIsCreateConnectionModalOpen(true);
            }} 
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('actions.addAnotherChild')}
          </Button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (showSettings) {
      return renderSettingsContent();
    } else if (showConnections) {
      return (
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Connections</h1>
            {/* No header button for connections page */}
          </div>
          {renderConnectionsContent()}
        </div>
      );
    } else {
      return renderDashboardContent();
    }
  };

  return (
    <SidebarProvider defaultOpen={!isMobile}>
      <div className="min-h-screen bg-background w-full">
        <Navbar />
        
        <div className="flex min-h-[calc(100vh-4rem)] pt-16 w-full">
          <AppSidebar 
            onCreateConnection={() => {
              console.log('DEBUG: AppSidebar onCreateConnection called, current states:', {
                isPhoneVerificationModalOpen,
                isCreateConnectionModalOpen
              });
              setReconnectData(null);
              // Only open create connection modal if verification modal is not open
              if (!isPhoneVerificationModalOpen) {
                setIsCreateConnectionModalOpen(true);
              } else {
                console.log('DEBUG: Not opening CreateConnectionModal because PhoneVerificationModal is open');
              }
            }}
          />
          
          <main className="flex-1 overflow-auto px-4 md:px-6 pb-6 pt-4 w-full">
            {renderContent()}
          </main>
        </div>
        
          <CreateConnectionModal
            isOpen={isCreateConnectionModalOpen}
            onClose={handleCreateConnectionModalClose}
            onSubmit={handleCreateConnection}
            onReconnect={handleReconnectConnection}
            connectionId={reconnectData?.id}
            connectionName={reconnectData?.name}
            showReconnectMode={!!reconnectData}
          />

        <PhoneVerificationModal
          isOpen={isPhoneVerificationModalOpen}
          onClose={() => {
            console.log('DEBUG: PhoneVerificationModal onClose called');
            // Only close the modal, without updating verification state
            // This allows the modal to appear again if user didn't complete verification
            setIsPhoneVerificationModalOpen(false);
            setIsCreateConnectionModalOpen(false);
            setReconnectData(null); // Clear reconnection state when closing modal
          }}
          onVerified={(phoneNumber) => {
            console.log("Phone verified:", phoneNumber);
            // Update verification state using centralized context
            updateWhatsAppPreferences({ isVerified: true });
            // Don't close modal automatically to allow user to see WhatsApp prompt
          }}
        />


      </div>
    </SidebarProvider>
  );
}
